<script lang="ts" setup>
import { defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useSeoMeta } from '@unhead/vue'

import { IMPACT_TYPES, type IProjectRequestParams } from '@/api/interfaces'
import { useEditorStore } from '@/stores/Editor'
import { usePublicData } from '@/stores/PublicData'
import { EVENT_TYPES, useEditorFrame } from '@/composables/iframe'
import { inIFrame } from '@/helpers/utils'
import PublicHeader from '@/components/public-page/PublicHeader.vue'
import PublicHero from '@/components/public-page/PublicHero.vue'

const PublicTopStatCardList = defineAsyncComponent(
  () => import('@/components/public-page/PublicTopStatCardList.vue'),
)
const PublicTopStatCardListLoader = defineAsyncComponent(
  () => import('@/components/public-page/PublicTopStatCardListLoader.vue'),
)
const PublicComparisonCardRow = defineAsyncComponent(
  () => import('@/components/public-page/PublicComparisonCardRow.vue'),
)
const PublicComparisonCardLoader = defineAsyncComponent(
  () => import('@/components/public-page/PublicComparisonCardLoader.vue'),
)
const PublicMap = defineAsyncComponent(() => import('@/components/public-page/PublicMap.vue'))
const PublicMapLoader = defineAsyncComponent(
  () => import('@/components/public-page/PublicMapLoader.vue'),
)
const PublicGoalsAchievements = defineAsyncComponent(
  () => import('@/components/public-page/PublicGoalsAchievements.vue'),
)
const PublicLedger = defineAsyncComponent(() => import('@/components/public-page/PublicLedger.vue'))
const PublicLedgerLoader = defineAsyncComponent(
  () => import('@/components/public-page/PublicLedgerLoader.vue'),
)
const PublicCTA = defineAsyncComponent(() => import('@/components/public-page/PublicCTA.vue'))
const PublicFooter = defineAsyncComponent(() => import('@/components/public-page/PublicFooter.vue'))

const publicDataStore = usePublicData()
const editorStore = useEditorStore()
const route = useRoute()
const { dispatchMessage } = useEditorFrame()
const companyId: string = route.params.id as string
const projectParams: IProjectRequestParams = {
  statuses: ['approved', 'deprecated'],
}
const pageTitle = `${route.params.name}'s sustainable impact with Greenspark`
const pageDescription = `${route.params.name} has partnered with Greenspark to achieve their sustainable and environmental goals such as planting trees, recovering plastic, offsetting CO2. Check out their journey so far!`
const isInitialDataLoading = ref(true)
const canRenderMap = ref(false)
const isPurchasesLoading = ref(true)
const isTopStatsReady = ref(false)

const handleTopStatsReady = () => {
  isTopStatsReady.value = true
  window.requestAnimationFrame(() => {
    if (!isInitialDataLoading.value && isTopStatsReady.value) {
      canRenderMap.value = true
    }
  })
}

watch(isInitialDataLoading, async (isLoading) => {
  if (isLoading) {
    canRenderMap.value = false
    isTopStatsReady.value = false
    return
  }
})

const init = () => {
  publicDataStore.loading = true
  isInitialDataLoading.value = true
  isPurchasesLoading.value = true

  Promise.all([
    publicDataStore.fetchPublicData(companyId),
    editorStore.fetchEditorSettings(companyId),
    publicDataStore.fetchProjects({ ...projectParams }),
  ])
    .catch((error) => console.error(error))
    .finally(() => {
      isInitialDataLoading.value = false
      publicDataStore.loading = false

      if (inIFrame()) {
        dispatchMessage(window.parent, { type: EVENT_TYPES.INIT })
      }
    })

  publicDataStore.fetchPurchases().finally(() => {
    isPurchasesLoading.value = false
  })
}

onMounted(() => {
  init()
})

useSeoMeta({
  title: pageTitle,
  description: pageDescription,
  ogTitle: pageTitle,
  ogType: 'website',
  ogDescription: pageDescription,
  ogUrl: window.location.href,
  ogImage:
    'https://cdn.prod.website-files.com/611391a1477389e3857d8014/673c84ac1ebcf01d18a6d07d_OG%20Image.png',
})
</script>

<template>
  <div
    :class="[
      'public-page',
      editorStore.editor.background.theme,
      editorStore.editor.background.icon,
    ]"
  >
    <PublicHeader />
    <PublicHero
      :editor-settings="editorStore.editor.hero"
      :theme="editorStore.editor.background.theme"
    />
    <PublicTopStatCardList
      v-if="!isInitialDataLoading && publicDataStore.getTopStats"
      :color-settings="editorStore.editor.icon.impactColors"
      :icon-style="editorStore.editor.icon.style"
      :public-stat-data="publicDataStore.getPublicStats"
      :top-stat-data="publicDataStore.getTopStats"
      @ready="handleTopStatsReady"
    />
    <PublicTopStatCardListLoader v-if="isInitialDataLoading" />
    <PublicMap
      v-if="publicDataStore.projectList.length && !isInitialDataLoading && canRenderMap"
      :color-settings="editorStore.editor.icon.impactColors"
      :projects="publicDataStore.getActiveAndDeprecatedProjects"
    />
    <PublicMapLoader
      v-if="
        isInitialDataLoading ||
        (!isInitialDataLoading && !canRenderMap && publicDataStore.projectList.length)
      "
    />
    <template v-for="impactType in IMPACT_TYPES" :key="impactType">
      <PublicComparisonCardRow
        v-if="
          publicDataStore.getTopStats[impactType] &&
          editorStore.editor.icon.impactDisplay[impactType] &&
          !isInitialDataLoading &&
          canRenderMap
        "
        :color-settings="editorStore.editor.icon.impactColors"
        :icon-style="editorStore.editor.icon.style"
        :impact-type="impactType"
        :top-stat-data="publicDataStore.getTopStats"
      />
      <PublicComparisonCardLoader v-if="isInitialDataLoading || !canRenderMap" />
    </template>
    <PublicGoalsAchievements
      v-if="
        editorStore.editor.goalsAchievements[publicDataStore.lang]?.goals.length > 0 ||
        editorStore.editor.goalsAchievements[publicDataStore.lang]?.achievements.length > 0
      "
    />
    <PublicLedger
      v-if="!isPurchasesLoading"
      :purchases="publicDataStore.purchases"
      :transferred-receipts="publicDataStore.getTransferredImpactInvoices"
    />
    <PublicLedgerLoader v-else />
    <PublicCTA v-if="canRenderMap" />
    <PublicFooter v-if="canRenderMap" />
  </div>
</template>

<style lang="scss" scoped>
.public-page {
  background-color: var(--gray-light-f1);
  color: var(--ui-black);
  padding-top: 70px;

  &.dark {
    background-color: var(--ui-dark);
    color: var(--lightest);
  }

  &.custom {
    background-color: rgba(var(--custom-theme-color-rgb), 0.5);
    color: var(--lightest);
  }
}
</style>
